import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../../types/batch";

/**
 * A text socket that tolerates a NUMBER on the wire.
 *
 * Ingest columns are typed by the source, not by us. A vendor number, a postal
 * code or a company code that happens to be all digits arrives as a `number`,
 * and the very same column can be text in the next division's table — so the
 * socket has to accept both.
 *
 * This is not cosmetic. Step inputs ARE parsed through this schema before the
 * handler runs (`parseStepStartValue`, called per item by `executeStepBatch`) and
 * the resulting failure is NON-RETRYABLE, so one numeric cell aborts the whole
 * page. The service layer already coerces every one of these fields through its
 * own `text()` helper, so widening the socket only brings the declared contract
 * in line with what the runtime has always accepted.
 *
 * `preprocess` rather than `z.coerce.string()` (which happily turns `null` into
 * `"null"`) or a `z.union` (which degrades the inferred socket type to `any` and
 * breaks edge compatibility) — this keeps the socket typed `string`.
 *
 * ⚠️ Wrapper ORDER matters. Callers must write `numericText().nullable()
 * .optional()`, never `z.preprocess(fn, z.string().nullable().optional())`:
 * `isFieldOptional` in socket-type-inference.ts inspects only the OUTERMOST
 * wrapper and does not unwrap, so burying the optional inside the preprocess
 * makes the generated socket REQUIRED and fails publish validation on any
 * workflow that leaves it unwired. Guarded by this step's socket test.
 */
function numericText(inner: z.ZodString = z.string()) {
  return z.preprocess(
    (value) =>
      typeof value === "number" && Number.isFinite(value)
        ? String(value)
        : value,
    inner,
  );
}

/**
 * Upsert Vendor for Rexel
 *
 * ⚠️ DEPRECATED BY DESIGN — see the `deprecated` block below. This node encodes
 * one customer's AS400 vendor-master shape (division-prefixed vendor codes, a
 * separate company code, placeholder companies for rows that carry neither) and
 * exists only to carry that migration. Use "Upsert Vendor" for anything else.
 *
 * A COMBO leaf node that resolves a Company (`co_companies`), a Supplier
 * (`ap_suppliers`) and the supplier's Address (`core_addresses`) from ONE ingest
 * row, downstream of the "Observe DataSource Link" trigger (same fanout shape as
 * "Upsert Product"). Takes ONE item's fields as single-value input sockets; the
 * trigger's lockstep fanout zips its per-field `array<T>` outputs into one item
 * per row, and this step's `batch` config runs the per-item upserts in chunks
 * inside `executeStepBatch`.
 *
 * IDENTITY
 * --------
 * The supplier's `origin_id` is NOT the raw vendor number. It is the
 * division-prefixed vendor code: the vendor number zero-padded to 5 digits
 * behind the UPPER-CASED title of the DataSource behind the wired Data Source
 * Link (so vendor `43` on the `qe` source becomes `QE00043`). `origin_system` is
 * fixed to `as400_rexel` on both the supplier and the code-keyed company.
 *
 * WHY THIS IS A COMBO NODE (and not three chained nodes)
 * -----------------------------------------------------
 * The natural modelling is Upsert Company → Upsert Supplier → Upsert Address,
 * each consuming the previous node's generated id. That chain CANNOT be batched:
 * workflow fanout batching (v1) is only supported on a LEAF step with no
 * outgoing data edges (enforced by `validateBatchConfigs` in
 * `backend/.../workflows/publishValidation.ts`). It also could not express the
 * company reconciliation this migration needs — folding a placeholder company
 * into a code-keyed one has to happen in ONE transaction with the supplier
 * re-pointing, or a mid-flight failure leaves a supplier pointing at a company
 * that was already deleted.
 *
 * So the whole company → supplier → address hand-off happens in-process inside a
 * single leaf step (`upsertRexelVendorsFromItems`), one transaction per row.
 */

export const upsertVendorRexelStep = defineStepConfig({
  type: "upsert_vendor_rexel",
  name: "Upsert Vendor for Rexel",
  description:
    "Migration-specific vendor upsert: resolves a company (co_companies), supplier (ap_suppliers) and the supplier's address (core_addresses) from one AS400 vendor-master row, keying the supplier on a division-prefixed vendor code. Rows without a company code get a placeholder company that is folded into the real one once a code shows up. Deprecated by design — use Upsert Vendor instead.",
  category: "Data Ingest",
  libraryModule: "Platform",

  deprecated: {
    reason:
      "Hard-wired to one customer's AS400 vendor-master migration: the division-prefixed origin id, the fixed 'as400_rexel' origin system, the placeholder-company reconciliation and the 'N/A'/'ZZ' fillers are all specific to that source. Not a general vendor ingest step — use Upsert Vendor. Kept visible and executable only for that migration.",
  },

  inputSchema: z.object({
    // ── 1. Ingest provenance ────────────────────────────────────────────────
    // Recorded on co_company_data_links / ap_supplier_data_links so each
    // upserted row can be traced back to the ingest-DB row that produced it.
    // The link ALSO supplies the division: its DataSource's title, upper-cased,
    // is the prefix of every origin id this step writes.
    dataSourceLink: z.object({
      id: z.string().uuid(),
      dataSourceId: z.string().uuid(),
      schemaName: z.string(),
      tableName: z.string(),
    }),
    metadataId: z.string(),

    // ── 2. Company (co_companies) ───────────────────────────────────────────
    // Optional: a row without a company code gets a placeholder company keyed on
    // the supplier's own origin id, which a later row carrying the code folds in.
    // Numeric-tolerant: company codes are often all digits in the source.
    companyCode: numericText()
      .nullable()
      .optional()
      .describe(
        "Dedupe key for co_companies, origin system 'as400_rexel'. Omit to create a placeholder company for this supplier. Accepts a number.",
      ),
    companyName: z.string().nullable().optional(),
    companyEmail: z.string().nullable().optional(),

    // ── 3. Supplier (ap_suppliers) ──────────────────────────────────────────
    supplierNumber: numericText().describe(
      "Raw vendor number. Zero-padded to 5 digits behind the upper-cased DataSource title to form the supplier's origin id. Accepts a number.",
    ),
    supplierName: z.string().nullable().optional(),

    // ── 4. Supplier address (core_addresses) ────────────────────────────────
    // All optional: the migration's source rows are sparse, and a missing
    // required column is filled with a placeholder rather than dropping the row.
    supplierAdministrativeArea: z
      .string()
      .nullable()
      .optional()
      .describe("State/province — core_addresses.administrative_area."),
    supplierLocality: z
      .string()
      .nullable()
      .optional()
      .describe("City — core_addresses.locality."),
    supplierCountryCode: z
      .string()
      .length(2)
      .nullable()
      .optional()
      .describe(
        "ISO 3166-1 alpha-2 country code — exactly 2 characters. Anything else is ignored with a warning at runtime.",
      ),
    supplierAddress: z
      .string()
      .nullable()
      .optional()
      .describe("Street address — core_addresses.address_line1."),
    // Both numeric-tolerant: phone columns are frequently numeric, and a US ZIP
    // is a number in most sources (the failure that prompted this).
    supplierPhone: numericText().nullable().optional(),
    supplierPostalCode: numericText()
      .nullable()
      .optional()
      .describe("core_addresses.postal_code. Accepts a number."),
    supplierContactName: z
      .string()
      .nullable()
      .optional()
      .describe("core_addresses.recipient_name."),
  }),

  outputSchema: z.object({
    supplierOriginId: z
      .string()
      .nullable()
      .describe(
        "The normalized division-prefixed vendor code used as the supplier's origin id",
      ),
    companyId: z
      .string()
      .uuid()
      .nullable()
      .describe("The resolved company's id"),
    supplierId: z
      .string()
      .uuid()
      .nullable()
      .describe("The upserted supplier's id"),
    addressId: z
      .string()
      .uuid()
      .nullable()
      .describe("The supplier's primary address id"),
    companyCreated: z.boolean().describe("A Company was created"),
    companyUpdated: z.boolean().describe("An existing Company was updated"),
    supplierCreated: z.boolean().describe("A Supplier was created"),
    supplierUpdated: z.boolean().describe("An existing Supplier was updated"),
    placeholderCompanyMerged: z
      .boolean()
      .describe(
        "A placeholder company was folded into the code-keyed company and deleted",
      ),
    skipped: z
      .boolean()
      .describe(
        "Row skipped — no vendor number, no resolvable division, or no ingest provenance",
      ),
  }),

  // Batch-by-design like Upsert Vendor: this is a leaf built for the batched
  // Observe→Upsert backfill, so it ships a default `batch` config. `batch.size`
  // doubles as the Observe trigger's initial-backfill window size. Read by the
  // engine via `getBatchConfig(step.config)`, not as a normal config field.
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
